import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ProductDTOFromJSON } from '@atlassiansox/phobos-client-ts';

import { ActionButtons, EntityPageSkeleton, Item } from '@/components/primitive';
import { ExperienceErrorBoundary } from '@/components/primitive/error-boundary';
import { WithPageTitle } from '@/components/primitive/with-page-title';
import {
  useFetchErrorController,
  FilterStateProvider,
  useLocationController,
  useNavigationController,
} from '@/controllers';
import { apiFetch } from '@/services';
import { spaceTopComfortable, marginAuto, spaceTopCompact, breakWord } from '@/styles';
import { FetchError } from '@/services/api-service/api-service';
import { PageContentTabs } from '@/components/composed';
import { PageContentTabNames } from '@/components/composed/page-content-tabs/constants';

import { contentDashboardStyles, sectionTopStyles } from '../dashboard/styles';

export type ProductViewProps = {
  tabName?: PageContentTabNames;
};

const ProductView: React.FC<ProductViewProps> = ({ tabName }) => {
  const { publicId } = useParams<{ publicId: string }>();

  const { isDashboard } = useLocationController();
  const { onError } = useFetchErrorController();
  const { onNavigation } = useNavigationController();

  const { data: rawData, isInitialLoading: isLoading, error } = useQuery(
    ['product', publicId],
    () => apiFetch(`/product/${publicId}`),
    { onError }
  );

  const data = ProductDTOFromJSON(rawData);

  useEffect(() => {
    if (error instanceof FetchError && error.response.status === 404) {
      onNavigation('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (!rawData || isLoading || data === undefined) return <EntityPageSkeleton />;

  return (
    <ExperienceErrorBoundary>
      <WithPageTitle title={data.name} scope="Product">
        <div
          css={[marginAuto, isDashboard ? contentDashboardStyles : '']}
          data-testid="product-focus-container"
        >
          <section css={sectionTopStyles}>
            <Item title="Product">
              <div css={[spaceTopCompact, breakWord]}>
                <h3>{data.name}</h3>
              </div>
            </Item>
            <ActionButtons id={data.publicId} entityName={data.name} type="product" />
          </section>
          {data.description && (
            <section css={[sectionTopStyles, spaceTopComfortable]}>
              {data.description && (
                <Item title="Description">
                  <p>{data.description}</p>
                </Item>
              )}
            </section>
          )}
          <section css={spaceTopComfortable}>
            <FilterStateProvider>
              <PageContentTabs id={data.publicId} tabName={tabName} entityType="product" />
            </FilterStateProvider>
          </section>
        </div>
      </WithPageTitle>
    </ExperienceErrorBoundary>
  );
};

export default ProductView;
